import { useState, useEffect } from "react";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook para manejar la carga de usuarios
 * @param {Object} options - Opciones de configuración
 * @param {boolean} options.autoFetch - Si debe cargar usuarios automáticamente (default: true)
 * @param {function} options.onError - Callback para manejar errores
 * @param {function} options.onSuccess - Callback para cuando se cargan los usuarios exitosamente
 * @returns {Object} { users, loading, error, refetch }
 */
export const useUsers = (options = {}) => {
  const {
    autoFetch = true,
    onError,
    onSuccess,
  } = options;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${backendUrl}/api/users`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setUsers(data);
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message);
      
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoFetch) {
      fetchUsers();
    }
  }, [autoFetch]);

  return {
    users,
    loading,
    error,
    refetch: fetchUsers,
  };
};

/**
 * Hook simplificado para obtener solo la lista de usuarios
 * Compatible con el patrón existente de updateState({ users: data })
 * @returns {Array} Lista de usuarios
 */
export const useUsersList = () => {
  const { users } = useUsers();
  return users;
};

/**
 * Hook para obtener usuarios con formato específico (nombres completos)
 * @returns {Array} Lista de nombres de usuarios
 */
export const useUsersNames = () => {
  const { users } = useUsers();
  
  return users.map(user => {
    if (typeof user === 'string') {
      return user;
    }
    
    // Si el usuario es un objeto, intentar extraer el nombre completo
    if (user.fullName) return user.fullName;
    if (user.name) return user.name;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.username) return user.username;
    if (user.email) return user.email;
    
    return String(user);
  });
};

/**
 * Hook para obtener usuarios agrupados por rol o departamento
 * @param {string} groupBy - Campo por el cual agrupar ('role', 'department', etc.)
 * @returns {Object} Usuarios agrupados
 */
export const useUsersGrouped = (groupBy = 'role') => {
  const { users, loading, error } = useUsers();
  
  const groupedUsers = users.reduce((groups, user) => {
    const groupKey = user[groupBy] || 'Sin asignar';
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    
    groups[groupKey].push(user);
    return groups;
  }, {});
  
  return {
    groupedUsers,
    loading,
    error,
  };
};